import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, FolderOpen, Upload, Loader2, CheckCircle2,
  XCircle, Clock, Download, Trash2, Sparkles, Plus,
  RotateCw, Image as ImageIcon, Users, FileArchive, X, FileText,
  Shield, UserCircle2,
} from "lucide-react";
import { useGroupPortraitUploads } from "@/context/GroupPortraitUploadContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All operator-visible timestamps render in Sydney time regardless of
// the browser's local clock — every user of the app is coordinating
// off the same shoot schedule out of AU.
const TZ = "Australia/Sydney";
const DATE_FMT = new Intl.DateTimeFormat("en-AU", {
  timeZone: TZ,
  day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
  hour12: false,
});
const formatSydney = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return DATE_FMT.format(d) + " AEST/AEDT";
};

const STATUS_STYLES = {
  waiting:   { icon: Clock,         color: "text-slate-300",   bg: "bg-slate-800/60",  label: "Waiting" },
  uploading: { icon: Upload,        color: "text-amber-300",   bg: "bg-amber-950/60",  label: "Uploading" },
  queued:    { icon: Clock,         color: "text-slate-400",   bg: "bg-slate-800/60",  label: "Queued" },
  running:   { icon: Loader2,       color: "text-cyan-300",    bg: "bg-cyan-950/60",   label: "Running", spin: true },
  done:      { icon: CheckCircle2,  color: "text-emerald-300", bg: "bg-emerald-950/60",label: "Ready" },
  failed:    { icon: XCircle,       color: "text-red-300",     bg: "bg-red-950/60",    label: "Failed" },
};

// The pipeline is a strict left-to-right sequence. Each key matches a
// `progress.stage` value emitted by the backend worker. `viewable`
// stages become clickable buttons once the task hits them.
const PIPELINE_STEPS = [
  { key: "upload",   label: "Upload",   icon: Upload,      viewable: false },
  { key: "generate", label: "Sheets",   icon: ImageIcon,   viewable: true  },
  { key: "cluster",  label: "Cluster",  icon: Users,       viewable: false },
  { key: "match",    label: "Match",    icon: Sparkles,    viewable: true  },
  { key: "ai",       label: "AI Fill",  icon: Sparkles,    viewable: true  },
  { key: "zip",      label: "Package",  icon: FileArchive, viewable: false },
  { key: "done",     label: "Ready",    icon: CheckCircle2, viewable: true },
];

// Everything after (or equal to) `stage` counts as "reached" for the
// stepper. A task in stage "match" lights up upload+sheets+cluster+match.
function stepIndex(stage) {
  const i = PIPELINE_STEPS.findIndex((s) => s.key === stage);
  return i < 0 ? 0 : i;
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-bold ${s.bg} ${s.color}`}
    >
      <Icon className={`h-3 w-3 ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

function StageStepper({ task, onOpenStage }) {
  const curStage = task.progress?.stage || task.status;
  const cur = stepIndex(curStage);
  const isFailed = task.status === "failed";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`task-stepper-${task.task_id}`}>
      {PIPELINE_STEPS.map((step, i) => {
        const reached = !isFailed && (i <= cur || task.status === "done");
        const active = !isFailed && i === cur && task.status !== "done";
        const done = i < cur || task.status === "done";
        // Users can inspect any viewable stage that the task has
        // already reached (i.e. has data behind it).
        const clickable = step.viewable && reached && onOpenStage;
        const Icon = step.icon;
        return (
          <div key={step.key} className="flex items-center gap-1">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onOpenStage(step.key)}
              className={[
                "inline-flex items-center gap-1 px-2 h-6 rounded-sm text-[10px] uppercase tracking-widest font-bold transition-colors",
                done   ? "bg-emerald-950/50 text-emerald-300 border border-emerald-900/40" :
                active ? "bg-cyan-950/50 text-cyan-200 border border-cyan-800/60" :
                         "bg-slate-900/40 text-slate-500 border border-slate-800",
                clickable ? "hover:brightness-125 cursor-pointer" : "cursor-default",
              ].join(" ")}
              data-testid={`task-step-${task.task_id}-${step.key}`}
              title={clickable ? `View ${step.label}` : step.label}
            >
              <Icon className={`h-3 w-3 ${active && step.key !== "done" ? "animate-pulse" : ""}`} />
              {step.label}
            </button>
            {i < PIPELINE_STEPS.length - 1 && (
              <span className={`h-px w-3 ${reached ? "bg-emerald-800" : "bg-slate-800"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function TaskCard({ task, onDownload, onDelete, onResume, onRetry, onOpenStage, onDownloadLog, viewerRole }) {
  const { progress = {}, num_group_photos, num_portraits, num_matched, num_files, has_zip, name, created_at, error } = task;
  const created = formatSydney(created_at);
  const pct = progress?.pct || 0;
  const stage = progress?.stage || task.status;
  const canResume = task.status === "uploading" && !task._localUploading;
  const canRetry = task.status === "failed";
  const isOrphaned = task._orphaned;
  // Only surface owner metadata when an admin is looking at a task
  // that belongs to someone else — no need to clutter the standard
  // user's own queue with their own email.
  const showOwnerBadge = viewerRole === "admin" && task.is_own === false && !!task.owner_email;
  return (
    <div
      className="border border-slate-800 bg-slate-900/40 rounded-md p-4 space-y-3"
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-slate-100 font-semibold truncate">{name}</h3>
            <StatusBadge status={task.status} />
            {showOwnerBadge && (
              <span
                className="inline-flex items-center gap-1 px-2 h-5 rounded-sm border border-indigo-800/60 bg-indigo-950/40 text-indigo-200 text-[10px] font-bold uppercase tracking-widest"
                data-testid={`task-owner-badge-${task.task_id}`}
                title={`Task owned by ${task.owner_email}`}
              >
                <UserCircle2 className="h-3 w-3" />
                {task.owner_email}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 font-mono">
            {created} · {num_group_photos} group / {num_portraits} portraits
            {task.status === "done" ? ` · ${num_matched}/${num_files} named` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canResume && (
            <button
              type="button"
              onClick={() => onResume(task)}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm border border-amber-800/60 bg-amber-950/40 text-amber-200 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-900/40"
              data-testid={`task-resume-${task.task_id}`}
              title="If uploads finished but the pipeline didn't start, resume it manually."
            >
              <RotateCw className="h-3 w-3" /> Resume
            </button>
          )}
          {canRetry && (
            <div className="flex items-center gap-1" data-testid={`task-retry-group-${task.task_id}`}>
              <button
                type="button"
                onClick={() => onRetry(task)}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-l-sm border border-cyan-800/60 bg-cyan-950/40 text-cyan-200 text-[11px] font-bold uppercase tracking-widest hover:bg-cyan-900/40"
                data-testid={`task-retry-${task.task_id}`}
                title="Re-queue from the last checkpoint. Resets attempt counters."
              >
                <RotateCw className="h-3 w-3" /> Retry
              </button>
              <select
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  e.target.value = "";
                  onRetry(task, v);
                }}
                className="h-8 pr-2 pl-2 rounded-r-sm border-l-0 border border-cyan-800/60 bg-cyan-950/40 text-cyan-200 text-[10px] font-bold uppercase tracking-widest hover:bg-cyan-900/40 focus:outline-none cursor-pointer"
                data-testid={`task-rewind-select-${task.task_id}`}
                title="Rewind to a specific phase, then retry"
                defaultValue=""
              >
                <option value="" disabled>Rewind…</option>
                <option value="zip">Restart ZIP</option>
                <option value="ai">Restart AI Fill</option>
                <option value="match">Restart Match</option>
                <option value="cluster">Restart Cluster</option>
                <option value="generate">Restart from Start</option>
              </select>
            </div>
          )}
          {task.status === "done" && has_zip && (
            <button
              type="button"
              onClick={() => onDownload(task)}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90"
              data-testid={`task-download-${task.task_id}`}
            >
              <Download className="h-3 w-3" /> ZIP
            </button>
          )}
          <button
            type="button"
            onClick={() => onDownloadLog(task)}
            className="text-slate-500 hover:text-slate-200 p-1"
            data-testid={`task-log-${task.task_id}`}
            title="Download audit log (for forensic on failed tasks)"
          >
            <FileText className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(task)}
            className="text-slate-500 hover:text-red-400 p-1"
            data-testid={`task-delete-${task.task_id}`}
            title={task.status === "uploading" ? "Cancel upload & delete" : "Delete task"}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <StageStepper task={task} onOpenStage={(k) => onOpenStage(task, k)} />
      {isOrphaned && (
        <p
          className="text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/40 rounded-sm p-2 flex items-center gap-2"
          data-testid={`task-orphan-hint-${task.task_id}`}
        >
          <RotateCw className="h-3.5 w-3.5 shrink-0 animate-spin" style={{ animationDuration: "2s" }} />
          Your upload tab was closed before finishing. The server is auto-resuming this task — no data is lost, files already uploaded to storage will be processed.
        </p>
      )}
      {(task.status === "running" || task.status === "uploading") && !isOrphaned && (
        <div className="space-y-1">
          <div className="h-1.5 bg-slate-800 rounded-sm overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${task.status === "uploading" ? "bg-amber-500" : "bg-cyan-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-slate-300 font-mono truncate" data-testid={`task-progress-${task.task_id}`}>
            <span className="text-slate-500 uppercase tracking-widest text-[9px]">{stage}</span>
            <span className="text-slate-600 mx-1.5">·</span>
            <span className="text-cyan-300">{pct}%</span>
            {progress?.message ? (
              <>
                <span className="text-slate-600 mx-1.5">·</span>
                <span className="text-slate-200">{progress.message}</span>
              </>
            ) : null}
          </p>
        </div>
      )}
      {task.status === "failed" && error && (
        <p className="text-[11px] text-red-400 bg-red-950/30 border border-red-900/40 rounded-sm p-2">
          {error}
        </p>
      )}
    </div>
  );
}

function StageViewerModal({ task, stage, onClose, onDownload }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    axios.get(`${API}/group-portraits/tasks/${task.task_id}/preview`)
      .then(({ data }) => { if (alive) setDetail(data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [task.task_id]);

  const step = PIPELINE_STEPS.find((s) => s.key === stage);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stage-viewer-modal"
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              {task.name} · Stage
            </p>
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              {step?.icon ? <step.icon className="h-4 w-4 text-primary" /> : null}
              {step?.label || stage}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 p-1"
            data-testid="stage-viewer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {loading && <p className="text-slate-400 text-sm">Loading…</p>}
          {err && <p className="text-red-400 text-sm">{err}</p>}
          {detail && !loading && (
            <>
              {(stage === "generate" || stage === "done") && (
                <StageSheets sheets={detail.sheets || []} />
              )}
              {(stage === "match" || stage === "ai" || stage === "done") && (
                <StageMatches matches={detail.matches || []} />
              )}
              {stage === "done" && (
                <div className="pt-4 mt-4 border-t border-slate-800 flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    <b className="text-emerald-300">{detail.num_matched}</b> of{" "}
                    <b>{detail.num_files}</b> portraits named.
                  </p>
                  <button
                    type="button"
                    onClick={() => onDownload(task)}
                    className="inline-flex items-center gap-1.5 px-4 h-9 rounded-sm bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90"
                    data-testid="stage-viewer-download"
                  >
                    <Download className="h-3.5 w-3.5" /> Download ZIP
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StageSheets({ sheets }) {
  if (!sheets.length) {
    return <p className="text-slate-500 text-sm">No composite sheets generated yet.</p>;
  }
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
        Composite sheets ({sheets.length})
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sheets.map((s, i) => (
          <div key={i} className="border border-slate-800 rounded-md overflow-hidden bg-slate-950">
            {s.url ? (
              <img
                src={s.url}
                alt={s.out_filename}
                loading="lazy"
                className="w-full object-contain max-h-72"
                data-testid={`stage-sheet-${i}`}
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-600 text-xs">
                Preview unavailable
              </div>
            )}
            <div className="p-2 space-y-1">
              <p className="text-[11px] font-mono text-slate-300 truncate">{s.source_filename}</p>
              <p className="text-[10px] text-slate-500">
                {s.face_count} faces · {s.name_count} named
              </p>
              {s.names?.length ? (
                <p className="text-[10px] text-slate-500 line-clamp-2">
                  {s.names.join(" · ")}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageMatches({ matches }) {
  if (!matches.length) {
    return <p className="text-slate-500 text-sm">No matches yet.</p>;
  }
  const named = matches.filter((m) => m.matched_name);
  const unnamed = matches.filter((m) => !m.matched_name);
  const fmtSim = (s) => (s == null ? "" : Number(s).toFixed(2));
  return (
    <div className="mt-4 space-y-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        Matches · {named.length}/{matches.length} named
      </p>
      <div className="max-h-64 overflow-y-auto border border-slate-800 rounded-md divide-y divide-slate-800/60">
        {named.slice(0, 200).map((m, i) => (
          <div key={i} className="flex items-center justify-between text-[11px] px-3 py-1.5 font-mono">
            <span className="text-slate-300 truncate">{m.source}</span>
            <span className="ml-3 flex items-center gap-2 shrink-0">
              <span className="text-emerald-300 truncate">→ {m.matched_name}</span>
              {m.similarity != null && (
                <span
                  className={
                    m.similarity >= 0.6
                      ? "text-emerald-400 text-[9px]"
                      : m.similarity >= 0.5
                      ? "text-amber-300 text-[9px]"
                      : "text-slate-500 text-[9px]"
                  }
                  title={
                    m.runner_up_name
                      ? `Runner-up: ${m.runner_up_name} (${fmtSim(m.runner_up_similarity)})`
                      : ""
                  }
                >
                  {fmtSim(m.similarity)}
                </span>
              )}
              <span className="text-slate-600 uppercase text-[9px]">
                {m.matched_via || ""}
              </span>
            </span>
          </div>
        ))}
      </div>
      {unnamed.length > 0 && (
        <details className="text-[11px]" open>
          <summary className="text-amber-400 cursor-pointer uppercase tracking-widest text-[10px] font-bold">
            Unmatched — needs manual review · {unnamed.length}
          </summary>
          <p className="text-[10px] text-slate-500 mt-1 mb-2">
            These portraits couldn&apos;t be confidently matched. Common reasons:
            person not in the group photo, low face similarity, or two candidates
            were equally likely (ambiguous).
          </p>
          <div className="border border-slate-800 rounded-md divide-y divide-slate-800/60 max-h-40 overflow-y-auto">
            {unnamed.slice(0, 200).map((m, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] px-3 py-1.5 font-mono">
                <span className="text-slate-300 truncate">{m.source}</span>
                <span className="text-amber-400 truncate ml-3">{m.error || "no match"}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function GroupPortraitTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [groupFiles, setGroupFiles] = useState([]);
  const [portraitFiles, setPortraitFiles] = useState([]);
  const [stageView, setStageView] = useState(null); // { task, stage }
  const autoResumedRef = useRef(new Set());
  const groupRef = useRef(null);
  const portraitRef = useRef(null);
  const { uploads, enqueueTask, cancelUpload } = useGroupPortraitUploads();
  const { user } = useAuth();
  const viewerRole = (user && user.role) || "standard";
  const isAdmin = viewerRole === "admin";

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/group-portraits/tasks`);
      setTasks(data.tasks || []);
      setLoadState("ok");
      setLoadError(null);
    } catch (err) {
      console.warn("tasks load", err.message);
      setLoadState("error");
      setLoadError(err.response?.data?.detail || err.message || "Couldn't load tasks");
    }
  };

  // Initial load + poll every 4 s. Also re-poll when upload state
  // flips so a just-finalised task shows up immediately.
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { load(); }, [Object.keys(uploads).length]);

  // Auto-resume orphan uploads: if the server has a task in
  // "uploading" that we don't have a live local upload session for
  // AND it's been in that state for >8 s (e.g. tab was refreshed
  // after all PUTs finished, or Cloudflare killed the /finalize
  // call), poke /finalize so the pipeline can pick it up. Guarded
  // with a Set so we only try once per session per task.
  useEffect(() => {
    for (const t of tasks) {
      if (t.status !== "uploading") continue;
      if (uploads[t.task_id]) continue;           // still uploading locally
      if (autoResumedRef.current.has(t.task_id)) continue;
      const updated = t.updated_at ? new Date(t.updated_at).getTime() : 0;
      if (Date.now() - updated < 8_000) continue;  // give it a moment
      autoResumedRef.current.add(t.task_id);
      axios.post(`${API}/group-portraits/tasks/${t.task_id}/finalize`)
        .then(() => {
          toast.success(`Resumed "${t.name}"`);
          load();
        })
        .catch((err) => {
          console.warn("auto-resume failed", err?.response?.data || err.message);
          autoResumedRef.current.delete(t.task_id); // allow manual retry
        });
    }
  }, [tasks, uploads]);

  const startTask = async () => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error("Please give the task a name — it becomes the ZIP filename.");
      return;
    }
    if (groupFiles.length === 0) {
      toast.error("Pick at least one group photo");
      return;
    }
    if (portraitFiles.length === 0) {
      toast.error("Pick the portrait folder to rename");
      return;
    }
    // Confirm the operator got the name right — the ZIP will be
    // exported as `<Name>_G2P.zip`, so a typo lives on the delivery.
    const ok = window.confirm(
      `Confirm task name: "${trimmed}"\n\n` +
      `The final ZIP will be delivered as:\n    ${trimmed}_G2P.zip\n\n` +
      `Files to upload: ${groupFiles.length} group photo${groupFiles.length === 1 ? "" : "s"} + ` +
      `${portraitFiles.length} portrait${portraitFiles.length === 1 ? "" : "s"}.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    // Non-blocking — the upload continues in the global provider even
    // if the user navigates away. Close the form immediately.
    enqueueTask({ name: trimmed, groupFiles, portraitFiles });
    setName("");
    setGroupFiles([]);
    setPortraitFiles([]);
    setCreating(false);
    load();
  };

  // Merge server-side task state with in-flight upload progress so
  // "Uploading 47/259" shows in the same card even before finalize.
  const mergedTasks = tasks.map((t) => {
    const up = uploads[t.task_id];
    if (!up || up.status === "done") {
      // Server-side task without an active client upload — if it's
      // still stuck in "uploading" the tab was closed / refreshed
      // mid-flight. Flag it so the UI can surface a clear "resuming"
      // hint instead of showing stale "Uploading 12/47" progress.
      const orphaned = t.status === "uploading";
      return {
        ...t,
        _localUploading: false,
        _orphaned: orphaned,
        progress: orphaned
          ? { ...(t.progress || {}), message: "Upload session was lost — resuming automatically…" }
          : t.progress,
      };
    }
    const total = up.total || 1;
    const pct = Math.round(100 * (up.done + up.failed) / total);
    return {
      ...t,
      status: "uploading",
      _localUploading: true,
      progress: {
        stage: "upload", pct,
        message: `Uploading ${up.done + up.failed}/${total}` +
                 (up.failed > 0 ? ` · ${up.failed} failed (will retry)` : ""),
      },
    };
  });
  // Also surface any active uploads that haven't landed in /tasks yet
  // (e.g. within the first ~4 s after init), and "waiting" placeholder
  // rows for tasks queued behind another still-uploading task.
  for (const [tid, up] of Object.entries(uploads)) {
    if (!mergedTasks.find((t) => t.task_id === tid)) {
      const total = up.total || 1;
      const pct = up.status === "waiting"
        ? 0
        : Math.round(100 * (up.done + up.failed) / total);
      const isWaiting = up.status === "waiting";
      mergedTasks.unshift({
        task_id: tid, name: up.name || "New task",
        status: isWaiting ? "waiting" : "uploading",
        _localUploading: !isWaiting,
        _placeholder: !!up.placeholder,
        progress: {
          stage: isWaiting ? "waiting" : "upload",
          pct,
          message: isWaiting
            ? `Waiting for previous upload to finish (${total} file${total === 1 ? "" : "s"} queued)`
            : `Uploading ${up.done + up.failed}/${total}`,
        },
        num_group_photos: 0, num_portraits: total,
        num_matched: 0, num_files: 0, has_zip: false,
      });
    }
  }

  const onResume = async (task) => {
    try {
      await axios.post(`${API}/group-portraits/tasks/${task.task_id}/finalize`);
      toast.success("Task resumed — pipeline is picking it up");
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "resume failed";
      toast.error(`Couldn't resume: ${msg}`);
    }
  };

  const onRetry = async (task, rewind = null) => {
    const label = rewind ? `rewind to ${rewind}` : "retry";
    if (rewind) {
      const ok = window.confirm(
        `Rewind "${task.name}" to the ${rewind.toUpperCase()} phase and re-run?\n\n` +
        `Any work saved AFTER this phase will be discarded.`
      );
      if (!ok) return;
    }
    try {
      const url = rewind
        ? `${API}/group-portraits/tasks/${task.task_id}/retry?rewind=${rewind}`
        : `${API}/group-portraits/tasks/${task.task_id}/retry`;
      const { data } = await axios.post(url);
      const from = data?.resume_from || "start";
      toast.success(rewind
        ? `Rewound to ${from} — worker will restart this phase`
        : (from === "start" ? "Retrying from scratch" : `Retrying — resuming from ${from} checkpoint`));
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || `${label} failed`;
      toast.error(`Couldn't ${label}: ${msg}`);
    }
  };

  const onOpenStage = (task, stage) => setStageView({ task, stage });

  const onDownloadLog = async (task) => {
    // Placeholder tasks (still queued client-side) have no server log yet.
    if (task._placeholder) {
      toast.info("Log becomes available after uploads start");
      return;
    }
    try {
      const res = await axios.get(
        `${API}/group-portraits/tasks/${task.task_id}/log`,
        { responseType: "blob" },
      );
      // Extract filename from Content-Disposition, fall back to task name.
      let fname = `${(task.name || task.task_id).replace(/[^\w.-]+/g, "_")}_G2P.log`;
      const cd = res.headers?.["content-disposition"];
      const m = cd && /filename="?([^";]+)"?/.exec(cd);
      if (m) fname = m[1];
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
    } catch (err) {
      const msg = err.response?.status === 404
        ? "No log available yet"
        : (err.response?.data?.detail || err.message || "download failed");
      toast.error(`Couldn't download log: ${msg}`);
    }
  };

  const onDownload = async (task) => {
    try {
      const { data } = await axios.get(`${API}/group-portraits/tasks/${task.task_id}/download`);
      // Trigger a download in a new tab.
      const a = document.createElement("a");
      a.href = data.url;
      a.download = data.filename;
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "download failed";
      toast.error(`Couldn't get download link: ${msg}`);
    }
  };

  const onDelete = async (task) => {
    // Client-side placeholder task waiting in the local upload queue —
    // has no server-side counterpart yet, so just cancel it locally.
    if (task._placeholder) {
      if (!window.confirm(`Cancel queued task "${task.name}"?`)) return;
      cancelUpload(task.task_id);
      toast.success("Queued task cancelled");
      return;
    }
    const isUploading = task.status === "uploading";
    const confirmMsg = isUploading
      ? `Cancel upload of "${task.name}"?\n\nThe upload will stop and the task will be deleted.`
      : `Delete "${task.name}" and its result ZIP?`;
    if (!window.confirm(confirmMsg)) return;
    // Stop any client-side PUT workers BEFORE deleting server-side —
    // otherwise the workers keep firing PUTs against a task that no
    // longer exists.
    cancelUpload(task.task_id);
    try {
      await axios.delete(`${API}/group-portraits/tasks/${task.task_id}`);
    } catch (err) {
      if (err.response?.status !== 404) {
        const msg = err.response?.data?.detail || err.message || "delete failed";
        toast.error(msg);
        return;
      }
      // 404 = already gone (deleted by another tab / previous action) — treat as success
    }
    setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
    toast.success(isUploading ? "Upload cancelled and task deleted" : "Task deleted");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="group-portrait-tasks-page">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
              data-testid="tasks-back"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-[11px] uppercase tracking-widest">Back</span>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Group → Portrait Tasks
                {isAdmin && (
                  <span
                    className="inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-indigo-800/60 bg-indigo-950/40 text-indigo-200 text-[10px] font-bold uppercase tracking-widest"
                    data-testid="admin-scope-badge"
                    title="You are viewing every operator's tasks"
                  >
                    <Shield className="h-3 w-3" /> Admin · All Users
                  </span>
                )}
              </h1>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {isAdmin
                  ? "Showing every operator's queue. Actions here affect the task owner's job."
                  : "Queue up jobs; they run in the background. ZIPs kept for 7 days."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-primary text-primary-foreground text-[12px] font-bold uppercase tracking-widest hover:bg-primary/90"
            data-testid="task-new-btn"
          >
            <Plus className="h-4 w-4" />
            New Task
          </button>
        </div>

        {/* Create form */}
        {creating && (
          <div className="mb-6 border border-primary/30 bg-primary/5 rounded-md p-4 space-y-3" data-testid="task-create-form">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 block mb-1">
                Task name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. RedBacksFC"
                className="w-full h-9 px-3 rounded-sm border border-slate-800 bg-slate-950 text-slate-100 text-sm focus:outline-none focus:border-primary"
                data-testid="task-name-input"
                required
              />
              <p className="text-[10px] text-slate-500 mt-1 font-mono" data-testid="task-name-preview">
                ZIP filename will be:{" "}
                <span className={name.trim() ? "text-emerald-300" : "text-amber-400"}>
                  {(name.trim() || "‹task-name›")}_G2P.zip
                </span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 block mb-1">
                  Group photos folder
                </label>
                <input
                  ref={groupRef}
                  type="file"
                  webkitdirectory="true"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const arr = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
                    setGroupFiles(arr);
                  }}
                  data-testid="task-group-input"
                />
                <button
                  type="button"
                  onClick={() => groupRef.current?.click()}
                  className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                  data-testid="task-group-pick"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  {groupFiles.length > 0 ? `${groupFiles.length} group photo${groupFiles.length === 1 ? "" : "s"}` : "Pick group photos"}
                </button>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 block mb-1">
                  Portraits folder to rename
                </label>
                <input
                  ref={portraitRef}
                  type="file"
                  webkitdirectory="true"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const arr = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
                    setPortraitFiles(arr);
                  }}
                  data-testid="task-portrait-input"
                />
                <button
                  type="button"
                  onClick={() => portraitRef.current?.click()}
                  className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                  data-testid="task-portrait-pick"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  {portraitFiles.length > 0 ? `${portraitFiles.length} portrait${portraitFiles.length === 1 ? "" : "s"}` : "Pick portrait folder"}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setCreating(false); setGroupFiles([]); setPortraitFiles([]); setName(""); }}
                className="px-3 h-9 rounded-sm text-slate-400 hover:text-slate-200 text-[11px] font-bold uppercase tracking-widest"
                data-testid="task-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={startTask}
                disabled={groupFiles.length === 0 || portraitFiles.length === 0}
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-sm bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90 disabled:opacity-50"
                data-testid="task-submit"
              >
                <Upload className="h-3.5 w-3.5" /> Queue Task
              </button>
            </div>
          </div>
        )}

        {/* Task list */}
        {loadState === "error" ? (
          <div
            className="border border-red-900/40 bg-red-950/30 rounded-md p-4 mb-3"
            data-testid="tasks-load-error"
          >
            <p className="text-red-300 text-sm font-semibold mb-1">
              Couldn&apos;t load your tasks
            </p>
            <p className="text-red-400/80 text-[11px] font-mono mb-3">{loadError}</p>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-red-900/60 text-red-100 text-[11px] font-bold uppercase tracking-widest hover:bg-red-900"
              data-testid="tasks-load-retry"
            >
              <RotateCw className="h-3 w-3" /> Retry
            </button>
          </div>
        ) : loadState === "loading" && mergedTasks.length === 0 ? (
          <div className="border border-dashed border-slate-800 rounded-md p-12 text-center">
            <Loader2 className="h-6 w-6 text-slate-600 mx-auto mb-2 animate-spin" />
            <p className="text-slate-500 text-sm">Loading your tasks…</p>
          </div>
        ) : mergedTasks.length === 0 ? (
          <div className="border border-dashed border-slate-800 rounded-md p-12 text-center" data-testid="tasks-empty">
            <FolderOpen className="h-8 w-8 text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No tasks yet. Click <b>New Task</b> to queue one.</p>
            <p className="text-slate-600 text-[11px] mt-2">
              Tasks are saved on the server for 7 days — they survive tab reloads and navigation.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {mergedTasks.map((t) => (
              <TaskCard
                key={t.task_id}
                task={t}
                onDownload={onDownload}
                onDelete={onDelete}
                onResume={onResume}
                onRetry={onRetry}
                onOpenStage={onOpenStage}
                onDownloadLog={onDownloadLog}
                viewerRole={viewerRole}
              />
            ))}
          </div>
        )}
      </div>
      {stageView && (
        <StageViewerModal
          task={stageView.task}
          stage={stageView.stage}
          onClose={() => setStageView(null)}
          onDownload={onDownload}
        />
      )}
    </div>
  );
}
